// Components==============
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faFolder } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import updateProject from 'actions/project/update';
import { AnimatePresence, motion } from 'framer-motion';
import { useAlert } from 'hooks/useAlert';
import { useProcessGroups } from 'hooks/useProcessGroups';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { framerFade, interactiveScale } from 'utilities/FramerAnimations';
import CircleButton from '../CircleButton/CircleButton';
import EvidencePreview from '../EvidencePreview/EvidencePreview';
import { ProjectProcessContext } from '../ProjectProcess/ProjectProcess';
import UploadModal from '../UploadModal/UploadModal';
import './EndpointDescription.module.scss';
import { useConditionalValues } from './useConditionalValues';
// =========================

const Item = ({
  item,
  type,
  project,
  readOnly,
}: {
  item: { value: string; id: number; done: boolean };
  type: 'delivery_goals' | 'sub_goals' | 'spark_goals';
  project?: ProjectProjectEntity;
  readOnly?: boolean;
}) => {
  const dispatch = useReduxAction();
  const translate = useTranslate();
  const alert = useAlert();
  const history = useHistory();

  const { processGroups } = useProcessGroups(project);
  const { setProcessGroups, handleProcessSave } = useContext(
    ProjectDetailsContext
  );

  const { conditionalProcessGroups } = useConditionalValues({
    reflectionProcessGroups: project ? processGroups : undefined,
    reflectionProject: project || undefined,
  });

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    const newValue = conditionalProcessGroups.map((group) => {
      const description = group.description as EndPointType;

      return group.id === 7 && group.process_type === 'App\\Phase'
        ? {
            ...group,
            description: {
              ...description,
              [type]: description[type]?.map((i) =>
                i.id === item.id ? { ...i, done: !i.done } : i
              ),
            },
          }
        : group;
    });

    // project page
    if (setProcessGroups) {
      setProcessGroups(newValue);
      handleProcessSave(newValue);
      return;
    }

    // reflection page
    dispatch(
      updateProject({
        id: project?.id,
        process: newValue,
      })
    )
      .then((r: ProjectProjectEntity & { process: EvidenceEntity[] }) => {
        //  needed because process id changes on save
        if (!setProcessGroups) {
          return history.push(
            `/groei/reflecteren/${r.id}/${
              r?.process?.[r.process?.length - 1].id
            }`
          );
        }
      })
      .catch(() => {
        alert(translate('general.error'));
      });
  };

  return (
    <motion.div
      styleName={`item ${item.done ? 'done' : ''}`}
      {...interactiveScale}
      onClick={handleClick}
      style={{ pointerEvents: readOnly ? 'none' : 'all' }}
    >
      <div styleName="check-circle">
        {!!item.done && (
          <motion.div {...framerFade}>
            <FontAwesomeIcon icon={faCheck} />
          </motion.div>
        )}
      </div>
      <p>{item.value}</p>
    </motion.div>
  );
};

export default function EndpointDescription({
  description,
  project,
  readOnly,
}: {
  description: EndPointType;
  project?: ProjectProjectEntity;
  readOnly?: boolean;
}) {
  const translate = useTranslate();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const {
    handleEvidenceUpload,
    loading,
    handleDeleteEvidenceAndFeedback,
  } = useContext(ProjectDetailsContext);

  const { processGroups } = useContext(ProjectProcessContext);

  const endProcess = processGroups?.find(
    (group) => group.id === 7 && group.process_type === 'App\\Phase'
  );

  const process = useRedux((state) =>
    endProcess?.processId
      ? state.process?.entities.byId[endProcess?.processId]
      : undefined
  );

  const isLocked = !!project?.locked_for_assessment;

  const userId = useRedux((state) => state.authentication?.user?.id);

  // const {
  //   // allProjectSparks: reflectionAllProjectSparks,
  //   groupedSparks: reflectionGroupedSparks,
  //   skillObj: reflectionSkillObj,
  // } = useContext(ReflectionItemContext);

  // const {
  //   // filteredProjectSparks: projectAllProjectSparks,
  //   groupedSparks: projectGroupedProjectSparks,
  //   skillObj: projectSkillObj,
  // } = useContext(ProjectProcessContext);

  // // const allProjectSparks =
  // //   projectAllProjectSparks || reflectionAllProjectSparks;
  // const groupedSparks = projectGroupedProjectSparks || reflectionGroupedSparks;
  // const skillObj = projectSkillObj || reflectionSkillObj;

  return (
    <div styleName="wrapper">
      {!!description.delivery_goals?.length && (
        <div>
          <h3>{translate('general.terms.delivery-goals')}</h3>
          <div styleName="items">
            {description.delivery_goals.map(
              (dg) =>
                !!dg && (
                  <Item
                    item={dg}
                    key={dg.id}
                    type="delivery_goals"
                    project={project}
                    readOnly={readOnly}
                  />
                )
            )}
          </div>
        </div>
      )}
      {!!description.sub_goals?.length && (
        <div>
          <h3>{translate('general.terms.sub-goals')}</h3>
          <div styleName="items">
            {description.sub_goals.map((dg) => (
              <Item
                item={dg}
                key={dg.id}
                type="sub_goals"
                project={project}
                readOnly={readOnly}
              />
            ))}
          </div>
        </div>
      )}
      {!!description.learning_goals?.length && (
        <div>
          <h3>Leerdoelen</h3>
          <div styleName="items">
            {description.learning_goals.map((dg) => (
              <Item
                item={dg}
                key={dg.id}
                type="learning_goals"
                project={project}
                readOnly={readOnly}
              />
            ))}
          </div>
        </div>
      )}
      {!!description.note && <p>{description.note}</p>}
      <div styleName="bar" />
      {!!process?.evidence?.length && (
        <EvidencePreview
          evidence={process.evidence}
          deleteCondition={
            project?.members.find((m) => m.membership === 'owner')?.user_id ===
            userId
          }
          handleDelete={isLocked ? undefined : handleDeleteEvidenceAndFeedback}
          // rootModal={rootModal}
          // disableComments={disableComments}
        />
      )}
      <div styleName="button-wrapper">
        <CircleButton
          inactive={loading}
          icon={faFolder}
          color="dark-grey"
          onClick={() => setModalIsOpen(true)}
        />
      </div>
      <AnimatePresence>
        {modalIsOpen && (
          <UploadModal
            setModalIsOpen={setModalIsOpen}
            handleUpload={(file) =>
              handleEvidenceUpload(
                typeof file === 'string'
                  ? { link: file, processId: process?.id! }
                  : { file, processId: process?.id! }
              )
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
}
